import React, { Dispatch, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { PostRequestModel, SpecificationsModel } from '../../../requestsModel'
import { MakeRequestContext } from '../../MakeRequestContext'
import { connect } from 'react-redux'
import errorCatcher from '../../../../../utils/errorCatcher'
import RequestForm from './RequestForm'
import { modalTypes } from '../../../../../enums/modalTypeEnum'
import fetchFactory from '../../../../../utils/fetchFactory'
import { configEnum } from '../../../../../enums/fetchFactoryEnum'
import { timeSlotEnum, weekDaysEnum } from '../../../../../enums/choicesEnum'
import { ASSURANCE_CONCERN } from '../Stepper'
import { requestPurpose } from '../../../../../enums/contractsEnum'
import { getValueCategorieDemandeTechnique } from '../../../../../enums/demandesTechniqueEnum'

interface Store {
	errorCatcher: (error: any, id: string) => void
}

const ERROR_CATCHER_ID: string = 'requestSpecifications'

type RequestSpecificationsProps = Store

const RequestSpecifications: React.FC<RequestSpecificationsProps> = (
	{
		errorCatcher
	}
) => {
	const { request, nextStep } = useContext(MakeRequestContext)
	const [loading, setLoading] = useState<boolean>(false)
	const intl = useIntl()
	const choice1: string = intl.formatMessage({
		id: 'requests.makeRequest.step4.keyExampleOption',
		defaultMessage: 'Je laisse un exemplaire de mes clés au bureau d\'accueil de mon YouFirst Manager',
		description: 'choice key'
	})
	const choice3: string = intl.formatMessage({
		id: 'requests.makeRequest.step4.advisorOption',
		defaultMessage: 'Je préfère être contacté par mon YouFirst Manager',
		description: 'choice advisor'
	})

	const isChoiceDates = (choice: string) => {
		return choice !== choice1 && choice !== choice3
	}

	const postRequestWeekDay = (values: SpecificationsModel, index: number) => {
		if (values.choicesOption && values.dateChoices && isChoiceDates(values.choicesOption) && Object.values(weekDaysEnum).includes(values.dateChoices[index].weekDay)) {
			return values.dateChoices[index].weekDay
		} else {
			return ''
		}
	}

	const postRequestTimeSlot = (values: SpecificationsModel, index: number) => {
		if (values.choicesOption && values.dateChoices && isChoiceDates(values.choicesOption) && Object.values(timeSlotEnum).includes(values.dateChoices[index].timeSlot)) {
			return values.dateChoices[index].timeSlot
		} else {
			return ''
		}
	}

	const onSubmit = (values: SpecificationsModel) => {
		setLoading(true)
		let categorieDemandePrivee: any = ''
		if (request.requestPurpose === requestPurpose.private && request.requestConcern) {
			categorieDemandePrivee = getValueCategorieDemandeTechnique(request.requestConcern)
		}
		const data: PostRequestModel = {
			eft_typedemande: request.requestType || '',
			eft_bienId: request.residence || '',
			eft_Categoriedelademande1: `${request.requestPurpose}_${request.requestConcern}`,
			eft_Locataire: request.interlocutor || '',
			eft_BailId: request.propertyConcerned || '',
			description: values.message || '',
			eft_categorie_demande_ec: categorieDemandePrivee,
			piecesjointes: values.attachment,
			eft_creneau_1_jour: postRequestWeekDay(values, 0),
			eft_creneau_1_matin_ou_apres_midi: postRequestTimeSlot(values, 0),
			eft_creneau_2_jour: postRequestWeekDay(values, 1),
			eft_creneau_2_matin_ou_apres_midi: postRequestTimeSlot(values, 1),
			eft_creneau_3_jour: postRequestWeekDay(values, 2),
			eft_creneau_3_matin_ou_apres_midi: postRequestTimeSlot(values, 2),
			eft_clefalaloge: values.choicesOption === choice1,
			eft_datedebutassurance: values.dateDebutAssurance || '',
			eft_datefinassurance: values.dateFinAssurance || ''
		}

		fetchFactory(
			`/api/ec_${process.env.REACT_APP_TARGET}/demande/createRequest`,
			{
				body: JSON.stringify({
					...data,
					contract: request.contract
				}),
				method: configEnum.post
			}
		)
			.then(
				() => {
					nextStep()
				}
			)
			.catch(
				(err) => {
					errorCatcher(err, ERROR_CATCHER_ID)
				}
			)
			.finally(
				() => {
					setLoading(false)
				}
			)
	}

	const validate = (values: SpecificationsModel) => {
		const errors: any = {}
		if (!values.message && request.requestConcern !== ASSURANCE_CONCERN) {
			errors.message = intl.formatMessage(
				{
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				}
			)
		}
		if (request.requestConcern === ASSURANCE_CONCERN) {
			if (!values.dateDebutAssurance || values.dateDebutAssurance.length === 0) {
				errors.dateDebutAssurance = intl.formatMessage(
					{
						id: 'errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					}
				)
			}
			if (!values.dateFinAssurance || values.dateFinAssurance.length === 0) {
				errors.dateFinAssurance = intl.formatMessage(
					{
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					}
				)
			}
			if (!!values.dateDebutAssurance && !!values.dateFinAssurance && values.dateDebutAssurance >= values.dateFinAssurance) {
				errors.dateDebutAssurance = intl.formatMessage(
					{
						id: 'errors.wrongDate',
						defaultMessage: 'Requis*',
						description: 'La date de fin ne peut pas être avant la date de début'
					}
				)
			}
			if (!values.attachment || (values.attachment && !values.attachment.length)) {
				errors.attachment = intl.formatMessage(
					{
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					}
				)
			}
		}

		return errors
	}

	return (
		<RequestForm
			loading={loading}
			onSubmit={onSubmit}
			validate={validate}
			fromType={modalTypes.demandes}
			request={request}
		/>
	)
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => (
	{
		errorCatcher: (error: any, id: string) => {
			dispatch(errorCatcher(error, id))
		}
	}
)

export default connect(null, mapDispatchToProps)(RequestSpecifications)
